import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import {
    Box,
    TextField,
    FormControl,
    Grid,
    Button,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Tooltip
} from "@mui/material";
import SelectableSearch from "../../../../Base/views/SelectableSearch";
import { AddPHAllowanceAction, UpdatePHAllowanceAction } from "../../../../../../store/Finance/PayRollTemplate/Action";
import { GetCustomEarningAction } from "../../../../../../store/Finance/Component/Action";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const PHAllowance = ({ editedRecord, setOpenPopup }) => {
    const dispatch = useDispatch();
    const initialValue = {
        ph_allowance_type: "",
        flat_amount: "",
        ph_allowance_earning_components: [],
        ph_rate: "",
        ph_allowance_configuration_pk: ""
    };
    const [phAllowance, setPhAllowance] = useState(initialValue);
    const [errors, setErrors] = useState({});
    const [earningData, setEarningData] = useState({
        load: true,
        values: [],
    });
    const { custom_earning_data } = useSelector(
        (state) => state?.FinanceReducer.ComponentReducer
    );
    useEffect(() => {
        if (editedRecord != null) {
            setPhAllowance({
                ...editedRecord,
                ph_allowance_configuration_pk: editedRecord?.ph_allowance_configuration_pk,
                ph_allowance_earning_components: editedRecord?.ph_allowance_earning_components && typeof editedRecord?.ph_allowance_earning_components === "string"
                    ? JSON.parse(editedRecord?.ph_allowance_earning_components)
                    : editedRecord?.ph_allowance_earning_components,
            });
        }
        dispatch(GetCustomEarningAction());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editedRecord]);

    useEffect(() => {
        if (custom_earning_data && custom_earning_data[0]?.earnings_json) {
            handleEarnings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [custom_earning_data]);

    const handleEarnings = () => {
        if (!custom_earning_data || !custom_earning_data[0]?.earnings_json) {
            setEarningData({ load: true, values: [] }); 
            dispatch(GetCustomEarningAction());
        } else {
            setEarningData({ load: false, values: custom_earning_data[0].earnings_json });
        }
    };
    const validate = (fieldValues = phAllowance) => {
        let temp = { ...errors };

        if ("ph_allowance_type" in fieldValues)
            temp.ph_allowance_type = fieldValues.ph_allowance_type ? "" : "PH allowance type is required.";

        if ("flat_amount" in fieldValues && phAllowance.ph_allowance_type === "Flat")
            temp.flat_amount = fieldValues.flat_amount ? "" : "Flat amount is required.";

        if ("ph_allowance_earning_components" in fieldValues && phAllowance.ph_allowance_type === "Variable")
            temp.ph_allowance_earning_components =
                fieldValues.ph_allowance_earning_components?.length === 0
                    ? "Earning is required"
                    : fieldValues.ph_allowance_earning_components === null
                        ? "Earning is required"
                        : "";
         if ("ph_rate" in fieldValues && phAllowance.ph_allowance_type === "Variable")
            temp.ph_rate = fieldValues.ph_rate ? "" : "PH rate is required.";

        setErrors({ ...temp });
        return Object.values(temp).every((x) => x === "");
    };
    const handleInput = (event, value) => {

        if (["ph_allowance_earning_components"].includes(event)) {
            if (value) {
                setPhAllowance({ ...phAllowance, [event]: value });
            } else {
                setPhAllowance({ ...phAllowance, [event]: "" });
            }
            Object.values?.(errors)?.find((res) => res !== "")?.length > 0 &&
                validate({ ...phAllowance, [event]: value ? value : "" });
        }
        else if (event.target.name === "ph_allowance_type") {
            if (event.target.value === "Flat") {
                setPhAllowance({
                    ...phAllowance,
                    [event.target.name]: event.target.value,
                    flat_amount: "",
                    ph_allowance_earning_components: [],
                    ph_rate: ""
                });
            } else {
                setPhAllowance({
                    ...phAllowance,
                    [event.target.name]: event.target.value,
                    flat_amount: "",
                });
            }
            setErrors({})
        }
        else {
            setPhAllowance({
                ...phAllowance,
                [event.target.name]: event.target.value,
            });
            Object.values?.(errors)?.find((res) => res !== "")?.length > 0 &&
                validate({
                    ...phAllowance,
                    [event.target.name]: event.target.value,
                })
        }

    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (validate()) {
            const transformedVariableEarningComponents = JSON.stringify(
                phAllowance?.ph_allowance_earning_components?.map((item) => ({
                    name: item.name,
                    label: item.label,
                }))
            );
            if (editedRecord?.ph_allowance_configuration_pk) {
                dispatch(UpdatePHAllowanceAction({
                    ...phAllowance,
                    ph_allowance_earning_components: phAllowance?.ph_allowance_earning_components?.length >= 1 ? transformedVariableEarningComponents || null : null,
                    overtime_configuration_id: editedRecord?.overtime_configuration_pk
                }));
            }
            else {
                dispatch(
                    AddPHAllowanceAction({
                        ...phAllowance,
                        ph_allowance_earning_components: phAllowance?.ph_allowance_earning_components?.length >= 1 ? transformedVariableEarningComponents || null : null,
                    })
                );
            }
            setOpenPopup(false);
        }
    };
    return (
        <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={!!errors?.ph_allowance_type} size="small">
                        <InputLabel id="phAllowance-type-label">PH Allowance Type *</InputLabel>
                        <Select
                            labelId="phAllowance-type-label"
                            name="ph_allowance_type"
                            value={phAllowance?.ph_allowance_type}
                            label="PH Allowance Type"
                            onChange={handleInput}
                            sx={{ height: 42 }}
                        >
                            <MenuItem value="Flat">Flat</MenuItem>
                            <MenuItem value="Variable">Variable</MenuItem>
                        </Select>
                        <FormHelperText>{errors?.ph_allowance_type}</FormHelperText>
                    </FormControl>
                </Grid>
                {phAllowance?.ph_allowance_type === "Flat" && (
                    <Grid item xs={12} md={6}>
                        <TextField
                            size="small"
                            required
                            type="number"
                            value={phAllowance?.flat_amount}
                            onChange={handleInput}
                            variant="outlined"
                            fullWidth
                            id="flat_amount"
                            label="Amount / Day"
                            name="flat_amount"
                            autoComplete="flat_amount"
                            InputProps={{
                                endAdornment: (
                                    <Tooltip title="A flat amount paid per PH day worked. Half-day work earns 50% of the fixed amount.">
                                        <InfoOutlinedIcon fontSize="small" color="action" style={{ cursor: "pointer" }} />
                                    </Tooltip>
                                ),
                            }}
                            sx={{
                                "& .MuiInputBase-root": {
                                height: 40, 
                                },
                            }}
                            {...(errors.flat_amount && {
                                error: true,
                                helperText: errors.flat_amount,
                            })}
                        />
                    </Grid>
                )}
                {phAllowance?.ph_allowance_type === "Variable" && (
                    <>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <SelectableSearch
                                    isOptionEqualToValue={(option, value) =>
                                        option?.label === value?.label
                                    }
                                    multiple={true}
                                    limitTags={1}
                                    label={
                                        <span>
                                            Select an Earnings&nbsp;*&nbsp;
                                            <Tooltip title="Calculated using selected earning components. Pro-rated based on PH days worked during the pay period.">
                                                <InfoOutlinedIcon fontSize="small" color="action" style={{ verticalAlign: "middle", cursor: "pointer" }} />
                                            </Tooltip>
                                        </span>
                                    }
                                    fullWidth
                                    value={phAllowance?.ph_allowance_earning_components}
                                    loading={() => handleEarnings()}
                                    fieldLabel="label"
                                    required={false}
                                    variantType="outlined"
                                    name="ph_allowance_earning_components"
                                    size="small"
                                    onChangeEvent={handleInput}
                                    data={earningData}
                                    {...(errors?.ph_allowance_earning_components && {
                                        error: true,
                                        helperText: errors.ph_allowance_earning_components,
                                    })}
                                />
                            </FormControl>
                        </Grid>
                         <Grid item xs={12} md={6}>
                            <TextField
                                size="small"
                                required
                                type="number"
                                value={phAllowance?.ph_rate}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '' || Number(value) >= 1) {
                                        handleInput(e);
                                    }
                                }}
                                variant="outlined"
                                fullWidth
                                id="ph_rate"
                                label="PH Rate"
                                name="ph_rate"
                                autoComplete="ph_rate"
                                inputProps={{ min: 1 }}
                                InputProps={{
                                    endAdornment: (
                                        <Tooltip title="Applies a multiplier to selected earning components for public holiday work (e.g. a rate of 2 means 2× the total of those components).">
                                            <InfoOutlinedIcon fontSize="small" color="action" style={{ cursor: "pointer" }} />
                                        </Tooltip>
                                    ),
                                }}
                                 sx={{
                                    "& .MuiInputBase-root": {
                                    height: 40, 
                                    },
                                }}
                                {...(errors.ph_rate && {
                                    error: true,
                                    helperText: errors.ph_rate,
                                })}
                            />
                        </Grid>
                    </>
                )}
            </Grid>
            <Box mt={3} display="flex" justifyContent="flex-end">
                <Button
                    type="submit"
                    variant="contained"
                    size="medium"
                    className="mpl-primary-btn"
                >
                    {editedRecord ? "Update" : "Save"}
                </Button>
            </Box>
        </Box>
    );
};
export default PHAllowance;
